import { getStyleDeclaration } from '../get-style-declaration.js';
import { widgetSelector } from '../helpers/index.js';
import { shapeGetter } from './shape-getter/@impl/shape-getter.ts';

/**
 * Transform mini player widget description into list of style declarations.
 *
 * @example
 * {
 *    "id": "miniPlayerWidget_503264",
 *    "type": "miniPlayer",
 *    "x": 38,
 *    "y": 200,
 *    "width": 373,
 *    "height": 215,
 *    "style": {
 *        "top": "200px",
 *        "right": "529px"
 *    },
 *    "_isModified": false
 * }
 * @param widgetDescription
 */
export function miniPlayerWidget(widgetDescription) {
  const { placements, borderRadius = 0 } = widgetDescription;
  const selector = widgetSelector(widgetDescription);

  const rules = [
    ...placements.map(getStyleDeclaration.positions(selector)),
    ...placements.map(getStyleDeclaration.size(selector)),
  ];

  const { width, height, x, y } = widgetDescription;

  const mask = makeMask(
    {
      width,
      height,
      x,
      y,
      borderRadius,
    },
    {
      width: widgetDescription.parent.width,
      height: widgetDescription.parent.height,
    },
  );

  rules.push([
    `#${widgetDescription.slide.id}`,
    [
      ['mask-image', `url('${mask}')`],
      ['-webkit-mask-image', `url('${mask}')`],
      ['mask-size', '100% 100%'],
      ['-webkit-mask-size', '100% 100%'],
      ['mask-repeat', 'no-repeat'],
      ['-webkit-mask-repeat', 'no-repeat'],
    ],
  ]);

  widgetDescription.slide._displayZone = 'mini-player';

  Object.defineProperty(widgetDescription.slide, '_playerShape', {
    get() {
      return shapeGetter(widgetDescription, selector);
    },
  });

  /*
      // Apply very special transition to such slide, disabled due to numbers of issues
      {
        const transitionName = `${widgetDescription.slide.id}_fade`;
        const scale = Math.max(widgetDescription.slide.width / width, widgetDescription.slide.height / height);
        const translateX = (x/widgetDescription.slide.width*100).toFixed(3);
        const translateY = (y/widgetDescription.slide.height*100).toFixed(3);
        const duration = 700;

        rules.push([
        `#${widgetDescription.slide.id}`,
        [
                // fade rule, need to remake on changing translate rules
          ['transform-origin', 'top left'],
        ]
        ])
        widgetDescription.slide.transition.name = transitionName;
        widgetDescription.slide.transition.duration = duration;
        widgetDescription.slide.transition.timeout = {
          enter: duration,
          exit: duration,
        };
        // keep scale value for further usage in use-foreground.js
        widgetDescription.slide.scale = scale;

            widgetDescription.slide.parent.transitions.push({
        name: transitionName,
        delay: {
          enter: 0,
          exit: 0,
        },
        duration: {
          enter: `${duration}`,
          exit: `${duration}`,
        },
        timingFunction: {
          enter: 'ease-in',
          exit: 'ease-in',
        },
        rules: [
          ['opacity', [0.5, 0.99, 0.5]],
          [
            'transform',
            [
              `scale(${scale}) translate(-${translateX}%, -${translateY}%)`, // appear and entering
              'scale(1) translate(0, 0)', // enter, exiting
              `scale(${scale}) translate(-${translateX}%, -${translateY}%)`, // exit
            ],
          ],
        ],
      });
      }
  */

  return rules;
}

/**
 * It should form data image svg, applicable for mask-image property.
 *
 * @param {{width: number, height: number, x: number, y: number, borderRadius: number}} widgetParams
 * @param {{width: number, height: number}} slideParams
 * @return {string}
 */
function makeMask(widgetParams, slideParams) {
  const { height, x, y, borderRadius: bR } = widgetParams;

  const vert = widgetParams.height - bR * 2;
  const horiz = widgetParams.width - bR * 2;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${slideParams.width} ${
      slideParams.height
    }">
    <path stroke-width="0" d="
    M 0 0 h ${slideParams.width} v ${slideParams.height} h -${
      slideParams.width
    } Z
      M ${x + bR} ${height + y}
      h ${horiz}
      a ${bR},${bR} 0 0 0 ${bR},-${bR}
      v -${vert}
      a ${bR},${bR} 0 0 0 -${bR},-${bR}
      h -${horiz}
      a ${bR},${bR} 0 0 0 -${bR},${bR}
      v ${vert}
      a ${bR},${bR} 0 0 0 ${bR},${bR} Z
    "></path></svg>`.replace(/\n|\t/g, '');

  return `data:image/svg+xml;utf-8,<?xml version="1.0" encoding="utf-8"?>${svg}`;
}
